// JUST TO HANDLE REDUX LOGIC, DISPATCHES ACTIONS, ACCESSES GLOBAL STATE
// IMPORTANT	--DO NOT DO CALLS TO (API)-- PLEASE USE SERVICE FILE

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getShiftById,
  getShiftsForPeriod,
  createShift,
  updateExistingShift,
  deleteExistingShift,
  autoStaffShift,
  endEmployeeShift,
  startEmployeeShift,
  downloadShiftsReport,
  publishShifts,
  unpublishShifts,
  startEmployeeBreak,
  endEmployeeBreak,
} from './shiftsService';
import shiftsSlice from './shiftsSlice';
import { SHIFT_PUBLISH_STATUS } from '../../utils/constants/shiftConstants';

// TODO: Update the name of the thunk to newShifts/shifts when we complete the migration to the new shifts structure
export const fetchShift = createAsyncThunk('newShifts/fetchShift', async (shiftId) => {
  // Get a single shift

  const shift = await getShiftById(shiftId);
  return shift;
});

// Fetch all shifts for a venue within a period
export const fetchVenueShifts = createAsyncThunk('newShifts/fetchVenueShifts', async ({ venueId, period }) => {
  if (!period.start || !period.end) {
    throw new Error('Period is required to fetch shifts');
  }

  const shifts = await getShiftsForPeriod(venueId, period);
  return { shifts };
});

// Fetch shifts for a specific employee within a period
export const fetchEmployeeShifts = createAsyncThunk(
  'newShifts/fetchEmployeeShifts',
  async ({ venueId, period, employeeId }) => {
    if (!period.start || !period.end) {
      throw new Error('Period is required to fetch shifts');
    }
    if (!employeeId) {
      throw new Error('Employee ID is required to fetch employee shifts');
    }

    const shifts = await getShiftsForPeriod(venueId, period, employeeId);
    return { shifts, employeeId };
  }
);

// CREATE NEW SHIFT
export const createNewShift = createAsyncThunk('newShifts/createShift', async (shiftData) => {
  const venueId = shiftData.vid;
  const response = await createShift(shiftData, venueId);
  return response;
});

// UPDATE SHIFT
export const updateShift = createAsyncThunk('newShifts/updateShift', async ({ shiftId, updatedFields }) => {
  const response = await updateExistingShift(shiftId, updatedFields);
  return response;
});

export const deleteShift = createAsyncThunk('newShifts/deleteShift', async (id) => {
  const response = await deleteExistingShift(id);
  return response;
});

export const autoStaffShifts = createAsyncThunk(
  'newShifts/autoStaffShift',
  async ({ venueId, days }, { getState, dispatch }) => {
    const period = getState().newShifts.period;

    // Fetch existing shifts before auto-staff
    const oldShifts = await getShiftsForPeriod(venueId, period);
    const oldShiftIds = new Set(oldShifts.map((shift) => shift.id));

    // Call backend to auto-staff with provided days
    await autoStaffShift(venueId, { days });

    // Wait for Firebase to finish processing shifts
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Fetch updated shifts
    const newShifts = await getShiftsForPeriod(venueId, period);

    const addedShifts = newShifts.filter((shift) => !oldShiftIds.has(shift.id));

    // Immediately fetch shifts for the current period to ensure UI is up to date
    await dispatch(fetchVenueShifts({ venueId, period }));

    return {
      shifts: newShifts,
      meta: {
        addedCount: addedShifts.length,
      },
    };
  }
);

export const startShiftThunk = createAsyncThunk('newShifts/startShiftThunk', async (shiftId, { rejectWithValue }) => {
  try {
    const result = await startEmployeeShift(shiftId);
    return result;
  } catch (error) {
    // Extract just the error message to ensure we only pass serializable data
    const errorMessage = error?.message || 'Failed to start shift';
    return rejectWithValue(errorMessage);
  }
});

export const endShiftThunk = createAsyncThunk('newShifts/endShift', async (shiftId, { rejectWithValue }) => {
  try {
    const result = await endEmployeeShift(shiftId);
    return result;
  } catch (error) {
    // Extract just the error message to ensure we only pass serializable data
    const errorMessage = error?.message || 'Failed to end shift';
    return rejectWithValue(errorMessage);
  }
});

// Start break thunk
export const startBreakThunk = createAsyncThunk(
  'newShifts/startBreak',
  async ({ shiftId, breakType }, { rejectWithValue }) => {
    try {
      const result = await startEmployeeBreak(shiftId, breakType);
      // Return both the updated shift and the break type for status updates
      return {
        ...result,
        breakType,
      };
    } catch (error) {
      const errorMessage = error?.message || 'Failed to start break';
      return rejectWithValue(errorMessage);
    }
  }
);

// End break thunk
export const endBreakThunk = createAsyncThunk(
  'newShifts/endBreak',
  async ({ shiftId, breakId }, { rejectWithValue }) => {
    try {
      const result = await endEmployeeBreak(shiftId, breakId);
      return result;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to end break';
      return rejectWithValue(errorMessage);
    }
  }
);

// UPDATE DAILY NOTES - Using regular action instead of thunk
export const updateDailyNotes = (payload) => {
  return shiftsSlice.actions.updateDailyNotes(payload);
};

export const downloadShiftsPDF = (payload, fileName) => downloadShiftsReport(payload, fileName);

export const publishShiftsThunk = createAsyncThunk('newShifts/publishShifts', async (shiftIds, { rejectWithValue }) => {
  try {
    const response = await publishShifts(SHIFT_PUBLISH_STATUS.PUBLISHED, shiftIds);
    return response;
  } catch (error) {
    const errorMessage = error?.message || 'Failed to publish shifts';
    return rejectWithValue(errorMessage);
  }
});

export const unpublishShiftsThunk = createAsyncThunk(
  'newShifts/unpublishShifts',
  async (shiftIds, { rejectWithValue }) => {
    try {
      const response = await unpublishShifts(SHIFT_PUBLISH_STATUS.UNPUBLISHED, shiftIds);
      return response;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to unpublish shifts';
      return rejectWithValue(errorMessage);
    }
  }
);